/**
 * Repeat schedules: the rule, and the visits it implies. Decision 96.
 *
 * A schedule's visits are computed here and nowhere else. The job card's
 * Schedule tab, the agreement grid, the "Make this job repeat" footer and the
 * worker that creates the next job all call `repeatSlots`, so what the office
 * reads and what the worker does cannot disagree.
 *
 * Dates are calendar days (`YYYY-MM-DD`) with no zone. A visit is due on a
 * day, not at an instant; the account timezone only matters for "today".
 */

import { addCalendarDays } from './availability.js'

export type RepeatIntervalUnit = 'WEEK' | 'MONTH'
export type RepeatAnchor = 'DUE_DATE' | 'COMPLETED_DATE'
export type RepeatEnds = 'NEVER' | 'AFTER_VISITS' | 'ON_DATE'
export type RepeatBookWith = 'LAST_TECH' | 'PERSON' | 'UNASSIGNED'

export interface RepeatRule {
  intervalCount: number
  intervalUnit: RepeatIntervalUnit
  anchor: RepeatAnchor
  firstDueOn: string
  ends: RepeatEnds
  endAfterVisits: number | null
  endsOn: string | null
  /** A stopped schedule plans nothing further; its created jobs still show. */
  stopped: boolean
  /** The agreement's term end, when it has one. Nothing is planned past it. */
  termEndsOn?: string | null
}

/** What hangs off one slot, as `RepeatVisit` stores it. */
export interface RepeatVisitFact {
  sequence: number
  dueOn: string
  moved: boolean
  note: string | null
  skipped: boolean
  skipReason: string | null
  job: RepeatSlotJob | null
}

export interface RepeatSlotJob {
  id: string
  /** `JobStatus`. */
  status: string
  /** The calendar day it was completed, in the account zone. */
  completedOn: string | null
  /** The calendar day of its first live booking, if it has one. */
  bookedOn: string | null
}

/**
 * Where a slot is.
 *
 * `DONE` a completed job, `CREATED` a job not yet completed, `UNSUCCESSFUL` a
 * job marked unsuccessful, `SKIPPED` passed over on purpose, and — for a slot
 * with no job — `OVERDUE` when its day has gone, `NEXT` for the one to act on,
 * and `PLANNED` for the rest.
 */
export type RepeatSlotState =
  | 'DONE'
  | 'CREATED'
  | 'UNSUCCESSFUL'
  | 'SKIPPED'
  | 'OVERDUE'
  | 'NEXT'
  | 'PLANNED'

export interface RepeatSlot {
  sequence: number
  dueOn: string
  /** The day its job is created: `dueOn` less the lead time. */
  createOn: string
  state: RepeatSlotState
  /** The first slot with no job that is not skipped — the one to act on. */
  isNext: boolean
  /**
   * Counted from a completion that has not happened yet, so the date will
   * move. Only ever true under `COMPLETED_DATE`.
   */
  tentative: boolean
  moved: boolean
  note: string | null
  skipReason: string | null
  job: RepeatSlotJob | null
}

export interface RepeatSlotOptions {
  /** Today in the account zone. */
  today: string
  leadDays: number
  /** Plan no slot without a job due after this day. */
  until?: string
  /** Plan at most this many slots without a job. Default 2 past the next. */
  planned?: number
}

/** A hard ceiling, so a weekly rule over a long horizon cannot run away. */
const MAX_SLOTS = 520

/** `date` plus `months`, clamped to the month's last day (31 Jan → 28 Feb). */
export function addCalendarMonths(date: string, months: number): string {
  const [year, month, day] = date.split('-').map(Number) as [number, number, number]
  const target = new Date(Date.UTC(year, month - 1 + months, 1))
  const lastDay = new Date(
    Date.UTC(target.getUTCFullYear(), target.getUTCMonth() + 1, 0),
  ).getUTCDate()
  target.setUTCDate(Math.min(day, lastDay))
  return target.toISOString().slice(0, 10)
}

/** `date` plus `times` intervals of the rule. */
export function addInterval(
  date: string,
  interval: { intervalCount: number; intervalUnit: RepeatIntervalUnit },
  times = 1,
): string {
  const steps = interval.intervalCount * times
  return interval.intervalUnit === 'WEEK'
    ? addCalendarDays(date, steps * 7)
    : addCalendarMonths(date, steps)
}

/**
 * How many visits a year the rule makes, at its cadence: 4 for quarterly,
 * 26 for fortnightly. A rate, not a count of slots, so it can be fractional
 * (every 5 months is 2.4) and says nothing about when the rule ends.
 */
export function visitsAYear(interval: {
  intervalCount: number
  intervalUnit: RepeatIntervalUnit
}): number {
  return (interval.intervalUnit === 'WEEK' ? 52 : 12) / interval.intervalCount
}

/** "Monthly", "Every 6 months", "Every 2 weeks". */
export function intervalLabel(interval: {
  intervalCount: number
  intervalUnit: RepeatIntervalUnit
}): string {
  const { intervalCount: count, intervalUnit: unit } = interval
  if (count === 1) return unit === 'WEEK' ? 'Weekly' : 'Monthly'
  return `Every ${count} ${unit === 'WEEK' ? 'weeks' : 'months'}`
}

/**
 * The interval an imported ServiceM8 rrule describes, or null.
 *
 * Only `FREQ` and `INTERVAL` are read, because that is all the importer writes
 * (decision 41). A yearly rule is twelve months, so an annual inspection and a
 * twelve-monthly one are the same thing on the picker.
 */
export function intervalFromRrule(
  rrule: string,
): { intervalCount: number; intervalUnit: RepeatIntervalUnit } | null {
  const parts = new Map(
    rrule
      .replace(/^RRULE:/i, '')
      .split(';')
      .map((part) => part.split('=') as [string, string | undefined])
      .map(([key, value]) => [key.trim().toUpperCase(), (value ?? '').trim().toUpperCase()]),
  )

  const interval = Number(parts.get('INTERVAL') || '1')
  if (!Number.isInteger(interval) || interval < 1) return null

  switch (parts.get('FREQ')) {
    case 'MONTHLY':
      return { intervalCount: interval, intervalUnit: 'MONTH' }
    case 'YEARLY':
      return { intervalCount: interval * 12, intervalUnit: 'MONTH' }
    case 'WEEKLY':
      return { intervalCount: interval, intervalUnit: 'WEEK' }
    case 'DAILY':
      return interval % 7 === 0 ? { intervalCount: interval / 7, intervalUnit: 'WEEK' } : null
    default:
      return null
  }
}

/** Whether a slot with no job on this day is past the rule's end. */
function pastEnd(rule: RepeatRule, sequence: number, dueOn: string): boolean {
  if (rule.ends === 'AFTER_VISITS' && rule.endAfterVisits !== null) {
    if (sequence > rule.endAfterVisits) return true
  }
  if (rule.ends === 'ON_DATE' && rule.endsOn !== null && dueOn > rule.endsOn) return true
  if (rule.termEndsOn && dueOn > rule.termEndsOn) return true
  return false
}

/**
 * Every slot of a schedule worth drawing: the ones something hangs off, then
 * the next one to act on, then `planned` more (or everything up to `until`).
 *
 * Under `DUE_DATE` slot n is due `n - 1` intervals after the first, counted
 * from the first rather than from its neighbour so month-end clamping does
 * not creep (31 Jan, 28 Feb, 31 Mar). A moved slot moves only itself.
 *
 * Under `COMPLETED_DATE` a slot is due one interval after the previous job
 * was completed. Until it is, the date is counted from the previous due date
 * and marked tentative. A skipped or unsuccessful previous visit counts from
 * its due date, not tentatively — nobody is going to complete it.
 */
export function repeatSlots(
  rule: RepeatRule,
  visits: readonly RepeatVisitFact[],
  options: RepeatSlotOptions,
): RepeatSlot[] {
  const bySequence = new Map(visits.map((visit) => [visit.sequence, visit]))
  // Slots with a job or a skip are history and always drawn, even past an
  // end the rule has since been given.
  const lastFixed = visits.reduce(
    (max, visit) => (visit.job || visit.skipped ? Math.max(max, visit.sequence) : max),
    0,
  )
  const plannedLimit = options.planned ?? 2

  const slots: RepeatSlot[] = []
  let open = 0
  let nextFound = false

  for (let sequence = 1; sequence <= MAX_SLOTS; sequence += 1) {
    const visit = bySequence.get(sequence)
    const previous = slots[slots.length - 1]

    let ruleDue: string
    let tentative = false
    if (sequence === 1) {
      ruleDue = rule.firstDueOn
    } else if (rule.anchor === 'DUE_DATE' || !previous) {
      ruleDue = addInterval(rule.firstDueOn, rule, sequence - 1)
    } else if (previous.job?.completedOn && previous.state === 'DONE') {
      ruleDue = addInterval(previous.job.completedOn, rule)
    } else {
      ruleDue = addInterval(previous.dueOn, rule)
      tentative =
        previous.state !== 'SKIPPED' && previous.state !== 'UNSUCCESSFUL'
          ? true
          : previous.tentative
    }

    const dueOn = visit?.moved ? visit.dueOn : ruleDue
    const fixed = Boolean(visit?.job) || Boolean(visit?.skipped)

    if (!fixed) {
      if (sequence > lastFixed) {
        if (rule.stopped) break
        if (pastEnd(rule, sequence, dueOn)) break
        if (nextFound) {
          if (options.until !== undefined ? dueOn > options.until : open >= plannedLimit) break
        }
      }
    }

    let state: RepeatSlotState
    let isNext = false
    const job = visit?.job ?? null
    if (visit?.skipped) {
      state = 'SKIPPED'
    } else if (job) {
      state =
        job.status === 'COMPLETED' ? 'DONE' : job.status === 'CANCELLED' ? 'UNSUCCESSFUL' : 'CREATED'
    } else if (rule.stopped || pastEnd(rule, sequence, dueOn)) {
      // A gap left behind by a stopped or shortened rule, before its last
      // created job. Nothing will be created for it.
      state = 'SKIPPED'
    } else {
      if (!nextFound) {
        nextFound = true
        isNext = true
      } else {
        open += 1
      }
      state = dueOn < options.today ? 'OVERDUE' : isNext ? 'NEXT' : 'PLANNED'
    }

    slots.push({
      sequence,
      dueOn,
      createOn: addCalendarDays(dueOn, -options.leadDays),
      state,
      isNext,
      tentative: rule.anchor === 'COMPLETED_DATE' ? tentative : false,
      moved: visit?.moved ?? false,
      note: visit?.note ?? null,
      skipReason: visit?.skipReason ?? null,
      job,
    })
  }

  return slots
}

/** The slot to act on, if the schedule still has one. */
export function nextRepeatSlot(slots: readonly RepeatSlot[]): RepeatSlot | null {
  return slots.find((slot) => slot.isNext) ?? null
}

/**
 * Whether the worker should create this slot's job today.
 *
 * Only the next slot, so a long-overdue chain gets one job rather than a
 * board full of them, and never a tentative one: under `COMPLETED_DATE` the
 * previous job has to be finished before the next date means anything.
 */
export function repeatSlotReady(slot: RepeatSlot, today: string): boolean {
  return slot.isNext && !slot.tentative && slot.createOn <= today
}

/** The slots due inside `[from, to]`, both calendar days, inclusive. */
export function slotsBetween(
  slots: readonly RepeatSlot[],
  from: string,
  to: string,
): RepeatSlot[] {
  return slots.filter((slot) => slot.dueOn >= from && slot.dueOn <= to)
}

/** The last day of an agreement's term. A term with no end is drawn a year from its start. */
export function termEndOf(startsOn: string, endsOn: string | null): string {
  return endsOn ?? addCalendarDays(addCalendarMonths(startsOn, 12), -1)
}

/** The months of a term, as `YYYY-MM`, first to last. At most two years. */
export function termMonths(startsOn: string, endsOn: string): string[] {
  const months: string[] = []
  let cursor = `${startsOn.slice(0, 7)}-01`
  while (cursor.slice(0, 7) <= endsOn.slice(0, 7) && months.length < 24) {
    months.push(cursor.slice(0, 7))
    cursor = addCalendarMonths(cursor, 1)
  }
  return months
}

/** A `Date` from the database's `@db.Date` column, as `YYYY-MM-DD`. */
export function calendarDay(value: Date): string {
  return value.toISOString().slice(0, 10)
}

/** `YYYY-MM-DD` as the `Date` Prisma writes into a `@db.Date` column. */
export function calendarDate(day: string): Date {
  return new Date(`${day}T00:00:00.000Z`)
}
